// Cloudflare image-resizing loader for next/image.
//
// Rewrites a source to
//   ${CDN}/cdn-cgi/image/width=<w>,quality=<q>,format=auto/<target>
// so the Cloudflare edge produces a device-appropriate, re-encoded variant.
//
// Graceful passthrough: when NEXT_PUBLIC_CDN_BASE_URL is unset (CF proxy off /
// local dev) the original src is returned untouched, as are data:/blob: URIs
// and already-transformed URLs.

interface LoaderArgs {
  src: string;
  width: number;
  quality?: number;
}

export default function cloudflareLoader({ src, width, quality }: LoaderArgs): string {
  const q = quality || 75;

  if (
    src.startsWith('data:') ||
    src.startsWith('blob:') ||
    src.includes('/cdn-cgi/image/') ||
    // Auth'd private-media proxy — must be fetched by the browser (with the
    // session cookie), never re-fetched server-side by CF image resizing.
    src.includes('/api/media/')
  ) {
    return src;
  }

  const base = (process.env.NEXT_PUBLIC_CDN_BASE_URL || '').replace(/\/+$/, '');
  if (!base) return src; // CF resizing disabled → passthrough.

  const params = `width=${width},quality=${q},format=auto`;
  // Absolute URLs are passed to CF as-is; bare keys / root-relative paths are
  // resolved against the CDN base (private objects still require a signed URL —
  // this loader only handles public/CDN-served images).
  const target = /^https?:\/\//i.test(src) ? src : `${base}/${src.replace(/^\/+/, '')}`;

  return `${base}/cdn-cgi/image/${params}/${target}`;
}
